import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { CircularProgress } from "@mui/material";
import { useCookies } from "react-cookie";
import { fetchParticipationPerformance } from "ServerAPI/serverAPI";
import AttemptTimeline from "./AttemptTimeline";
import AttemptDetail from "./AttemptDetail";

// Backend sometimes sends raw extended-JSON (`{ "$date": "..." }` / `{ "$oid": "..." }`)
// instead of plain values — same pattern already handled in TrainingProgramProgress.js.
const unwrapDate = (value) => value?.$date || value || null;

// Normalizes the `userAnswers` object from a `fetchParticipationPerformance` response
// (type: "quiz") into a common per-attempt shape:
// - maxAttemptsHistory (when present & non-empty): full per-attempt quizResults, every attempt
//   reviewable. This is the Max-Attempts-specific field — distinct from the general, pre-existing
//   attemptCount/attempts fields used elsewhere for unrelated attempt reporting (e.g. FA monitoring).
// - Old flow (no maxAttemptsHistory): `quizResults` is always the authoritative latest-attempt
//   detail — this is a valid, expected shape (single-attempt records, or any record saved before
//   Max Attempts existed), not malformed data, so it must win over the detail-free legacy
//   `attempts` array below.
// - `attempts` (legacy, { score, date } only — no per-question detail): last resort, only used when
//   neither of the above is available.
const normalizeAttempts = (userAnswers) => {
  if (!userAnswers) return [];

  if (Array.isArray(userAnswers.maxAttemptsHistory) && userAnswers.maxAttemptsHistory.length > 0) {
    return userAnswers.maxAttemptsHistory
      .map((a) => {
        const qr = a.quizResults || {};
        const totalQuestions = qr.totalQuestions || 0;
        const correctQuestions = qr.correctQuestions || 0;
        const percentage =
          totalQuestions > 0 ? (correctQuestions / totalQuestions) * 100 : Number(a.score || 0);
        return {
          attemptNumber: a.attemptNumber,
          submittedAt: unwrapDate(a.submittedAt),
          passed: typeof a.passed === "boolean" ? a.passed : typeof qr.passed === "boolean" ? qr.passed : null,
          totalQuestions,
          correctQuestions,
          percentage,
          results: qr.results || [],
        };
      })
      .sort((a, b) => b.attemptNumber - a.attemptNumber);
  }

  if (userAnswers.quizResults) {
    const qr = userAnswers.quizResults;
    const totalQuestions = qr.totalQuestions || 0;
    const correctQuestions = qr.correctQuestions || 0;
    return [
      {
        attemptNumber: userAnswers.maxAttemptsUsed || 1,
        submittedAt: unwrapDate(qr.quizSubmittedAt),
        passed: typeof qr.passed === "boolean" ? qr.passed : null,
        totalQuestions,
        correctQuestions,
        percentage:
          totalQuestions > 0
            ? (correctQuestions / totalQuestions) * 100
            : Number(userAnswers.score ?? qr.score ?? 0),
        results: qr.results || [],
      },
    ];
  }

  // Legacy shape: just { score, date } per attempt, no per-question detail.
  if (Array.isArray(userAnswers.attempts) && userAnswers.attempts.length > 0) {
    return [...userAnswers.attempts]
      .map((a, idx) => ({
        attemptNumber: idx + 1,
        submittedAt: unwrapDate(a.date),
        passed: null,
        totalQuestions: 0,
        correctQuestions: 0,
        percentage: Number(a.score || 0),
        results: [],
      }))
      .sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
  }

  return [];
};

const AttemptHistoryModal = ({ show, handleClose, assessment }) => {
  const [cookies] = useCookies(["userId"]);
  const [loading, setLoading] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [selectedAttemptNumber, setSelectedAttemptNumber] = useState(null);

  useEffect(() => {
    if (!show) {
      setQuizQuestions([]);
      setAttempts([]);
      setSelectedAttemptNumber(null);
      return;
    }

    const contentId = assessment?.contentId;
    const courseId = assessment?.courseId;
    if (!contentId || !courseId) return;

    const fetchAttemptData = async () => {
      setLoading(true);
      try {
        const response = await fetchParticipationPerformance({
          courseId,
          contentId,
          userId: cookies.userId,
          type: "quiz",
        });
        if (response?.status === 200 && response.data) {
          setQuizQuestions(response.data.quiz?.questions || []);
          const normalized = normalizeAttempts(response.data.userAnswers);
          setAttempts(normalized);
          setSelectedAttemptNumber(normalized[0]?.attemptNumber ?? null);
        }
      } catch (err) {
        console.error("Error fetching attempt history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttemptData();
  }, [show, assessment?.contentId, assessment?.courseId, cookies.userId]);

  const selectedAttempt = attempts.find((a) => a.attemptNumber === selectedAttemptNumber) || attempts[0];

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {assessment?.contentTitle || assessment?.courseTitle || "Attempt History"}
          {attempts.length > 0 && (
            <span className="badge bg-primary ms-2">
              {attempts.length} attempt{attempts.length === 1 ? "" : "s"}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {loading ? (
          <div className="text-center p-4">
            <CircularProgress />
          </div>
        ) : attempts.length === 0 ? (
          <div className="text-center text-muted p-4">No attempts recorded yet.</div>
        ) : (
          <div className="d-flex gap-4 flex-wrap">
            <div style={{ flex: "0 0 220px", minWidth: "200px" }}>
              <AttemptTimeline
                attempts={attempts}
                selectedAttemptNumber={selectedAttempt?.attemptNumber}
                onSelect={setSelectedAttemptNumber}
              />
            </div>
            <div style={{ flex: "1 1 400px", minWidth: "280px" }}>
              <AttemptDetail attempt={selectedAttempt} questions={quizQuestions} loadingQuestions={false} />
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AttemptHistoryModal;
